/**
 * A frame chosen on the recommendations screen, on its way to the counter.
 *
 * It travels once, in the router state of the counter's history entry, and the counter clears that state the moment it takes the frame, so Back, Forward or a re-render cannot add it again. A refresh before then may lose it; nothing else depends on it. It carries identifiers, the opaque selection token and names to show — never a price, stock, a permission or a score. The counter asks the server for the price and availability, and checkout verifies the token.
 */
export const HANDOFF_STATE_KEY = 'frameRecommendationHandoff';

export interface FrameRecommendationHandoff {
  /** Random per click, so the counter takes each hand-off exactly once. */
  handoffId: string;
  customerId: string;
  /** The customer's name as the recommendations screen showed it; display only. */
  customerLabel: string | null;
  productId: string;
  variantId: string;
  selectionToken: string;
  /** What the card showed, for the basket line's name only. */
  productName: string;
  variantName: string | null;
  sku: string;
}

const text = (value: unknown): value is string => typeof value === 'string' && value !== '';
const optionalText = (value: unknown): value is string | null => value === null || text(value);

/** The router state that carries one hand-off. */
export function handoffState(handoff: FrameRecommendationHandoff): Record<string, unknown> {
  return { [HANDOFF_STATE_KEY]: handoff };
}

/** The hand-off in a history entry's state, or null when there is none or it is not whole. */
export function readHandoff(state: unknown): FrameRecommendationHandoff | null {
  if (typeof state !== 'object' || state === null) return null;
  const value = (state as Record<string, unknown>)[HANDOFF_STATE_KEY];
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Record<string, unknown>;

  if (
    !text(candidate.handoffId) ||
    !text(candidate.customerId) ||
    !optionalText(candidate.customerLabel) ||
    !text(candidate.productId) ||
    !text(candidate.variantId) ||
    !text(candidate.selectionToken) ||
    !text(candidate.productName) ||
    !optionalText(candidate.variantName) ||
    !text(candidate.sku)
  ) {
    return null;
  }

  return {
    handoffId: candidate.handoffId,
    customerId: candidate.customerId,
    customerLabel: candidate.customerLabel,
    productId: candidate.productId,
    variantId: candidate.variantId,
    selectionToken: candidate.selectionToken,
    productName: candidate.productName,
    variantName: candidate.variantName,
    sku: candidate.sku,
  };
}
